import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Chip,
  IconButton,
  Divider,
  Stack,
  Tooltip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  Warning as IncompleteIcon,
  Settings as SettingsIcon,
  Link as LinkIcon
} from '@mui/icons-material'
import { apiClient } from '../../services/api'
import { systemConnectionsApi, SystemConnection } from '../../services/api/systemConnectionsApi'
import { poolwerxColors } from '../../theme/colors'
import { useDataFetching } from '../../hooks/useDataFetching'
import ConnectSystemModal from '../../components/ConnectSystemModal'
import XeroReauthorizeDialog from '../../components/XeroReauthorizeDialog'
import DisconnectDialog from '../../components/DisconnectDialog'
import { openXeroLink, openLightspeedLink } from '../../utils/externalLinks'
import { generateXeroAppUrl, generateLightspeedTokensUrl, generateAdminFpUrl } from '../../utils/urls'
import { debugLog } from '../../utils/debugLog'
import { formatInUserTZ } from '../../utils/timezoneUtils'
import { getSystemIcon as getSharedSystemIcon } from '../../components/SystemIcons'

// System icons come from the shared components/SystemIcons.tsx (see getSystemIcon below).

// Extend the base SystemConnection interface with additional properties used in this component
interface ExtendedSystemConnection extends SystemConnection {
  status?: 'connected' | 'incomplete' | 'error'
  connection_status?: string
  last_tested_at?: string
  error_message?: string
  metadata: SystemConnection['metadata'] & {
    connection_method?: string
    scopes?: string[]
    short_code?: string
  }
}

interface FranchisePartner {
  id: number
  fp_code: string
  franchise_name: string
  country: string
  state: string
}

const ManageConnections: React.FC = () => {
  // Addressed by the per-tenure DB id (admin is id-only). The connection
  // sub-APIs are still fp_code-keyed (their id-qualification is the later
  // tenure-routing task), so we derive `fpCode` from the loaded FP below.
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Get return URL from location state, fallback to default
  const returnUrl = location.state?.returnUrl || '/admin/franchise-partners'
  
  const [franchisePartner, setFranchisePartner] = useState<FranchisePartner | null>(null)
  const [connections, setConnections] = useState<ExtendedSystemConnection[]>([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingConnection, setEditingConnection] = useState<ExtendedSystemConnection | null>(null)
  const [showReauthorizeDialog, setShowReauthorizeDialog] = useState(false)
  const [reauthorizeConnection, setReauthorizeConnection] = useState<ExtendedSystemConnection | null>(null)
  const [reauthorizeLoading, setReauthorizeLoading] = useState(false)
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [disconnectConnection, setDisconnectConnection] = useState<ExtendedSystemConnection | null>(null)
  const [testingConnections, setTestingConnections] = useState<{ [key: number]: boolean }>({})
  const [testResults, setTestResults] = useState<{ [key: number]: { success: boolean, message: string } }>({})
  const [disconnectingConnections, setDisconnectingConnections] = useState<{ [key: number]: boolean }>({})
  const [connectionAlert, setConnectionAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  // The shared store handle, derived from the loaded FP - used by all the
  // (still fp_code-keyed) connection sub-APIs and child dialogs.
  const fpCode = franchisePartner?.fp_code ?? ''

  // Combined data fetching function
  const fetchAllData = useCallback(async () => {
    if (!id) throw new Error('FP id is required')

    // Get franchise partner data from navigation state if available
    const stateData = location.state as {
      fp?: FranchisePartner,
      connectionTestSuccess?: { message: string, system: string, responseTime?: number },
      connectionTestError?: { message: string, system: string, status?: string }
    }

    let fpData: FranchisePartner
    if (stateData?.fp) {
      fpData = stateData.fp
    } else {
      debugLog(`Fetching franchise partner data for id: ${id}`)
      const fpResponse = await apiClient.get(`/franchise-partners/${id}`)
      debugLog('✅ FP data loaded')
      fpData = fpResponse.data
    }

    debugLog(`Fetching connections for: ${fpData.fp_code}`)
    const connectionsResponse = await systemConnectionsApi.getConnections(fpData.fp_code)
    debugLog('✅ Connections loaded')

    // Sort connections alphabetically by system name and cast to extended type
    const sortedConnections = connectionsResponse.data.sort((a, b) =>
      a.system_name.localeCompare(b.system_name)
    ) as ExtendedSystemConnection[]

    return {
      franchisePartner: fpData,
      connections: sortedConnections,
      stateData
    }
  }, [id, location.state])

  // Use useDataFetching hook for proper loading state management
  const { data, loading, error, refetch } = useDataFetching({
    fetchFn: fetchAllData,
    dependencies: [id],
    immediate: !!id,
    onSuccess: (result) => {
      setFranchisePartner(result.franchisePartner)
      setConnections(result.connections)

      // Handle connection test results from navigation state
      if (result.stateData?.connectionTestSuccess) {
        setConnectionAlert({
          type: 'success',
          message: result.stateData.connectionTestSuccess.message
        })
        // Clear alert after 10 seconds
        setTimeout(() => setConnectionAlert(null), 10000)
      } else if (result.stateData?.connectionTestError) {
        setConnectionAlert({
          type: 'error',
          message: result.stateData.connectionTestError.message
        })
        // Keep error alert until manually dismissed
      }
    },
    onError: (err) => {
      console.error('Error fetching data:', err)
    }
  })

  // Get list of all existing system names for the modal
  // Exclude all existing connections regardless of status to prevent duplicates
  const connectedSystems = connections
    .map(conn => conn.system_name)

  // Handle OAuth callback status messages
  useEffect(() => {
    const status = searchParams.get('status')
    const system = searchParams.get('system')
    const error = searchParams.get('error')

    if (status === 'cancelled' && system === 'xero') {
      setConnectionAlert({
        type: 'error',
        message: `⚠️ Xero connection was cancelled. You can try connecting again when ready.`
      })
      
      // Clear the URL parameters
      setSearchParams({})
      
      // Clear alert after 8 seconds
      setTimeout(() => setConnectionAlert(null), 8000)
    } else if (error) {
      setConnectionAlert({
        type: 'error',
        message: `❌ Connection failed: ${error}`
      })
      
      // Clear the URL parameters
      setSearchParams({})
      
      // Clear alert after 8 seconds
      setTimeout(() => setConnectionAlert(null), 8000)
    }
  }, [searchParams, setSearchParams])


  const handleConnect = async (systemName: string, connectionData: any): Promise<{ success: boolean; error?: string }> => {
    try {
      debugLog('Connecting system:', systemName, connectionData)
      
      let response
      
      if (systemName === 'lightspeed') {
        // Call Lightspeed connection endpoint
        response = await apiClient.post(
          `/connections/lightspeed/connect/${fpCode}`,
          {
            store_code: connectionData.storeCode,
            api_key: connectionData.apiKey
          }
        )
      } else if (systemName === 'skimmer') {
        // Call Skimmer connection endpoint
        response = await apiClient.post(
          `/connections/skimmer/connect/${fpCode}`,
          {
            account_id: connectionData.accountId,
            api_key: connectionData.apiKey
          }
        )
      } else {
        console.error('Unsupported system:', systemName)
        return { success: false, error: `Unsupported system: ${systemName}` }
      }
      
      if (response.data.success) {
        // Show success message
        setConnectionAlert({
          type: 'success',
          message: response.data.message || `Successfully connected ${systemName}`
        })
        
        // Refresh data
        await refetch()
        
        // Clear alert after 5 seconds
        setTimeout(() => setConnectionAlert(null), 5000)
        
        return { success: true }
      } else {
        // Return error to modal
        return { 
          success: false, 
          error: response.data.message || 'Connection failed' 
        }
      }
      
    } catch (error: any) {
      console.error(`Failed to connect ${systemName}:`, error)
      
      // Return error details to modal
      const errorMessage = error.response?.data?.detail || error.message || `Failed to connect ${systemName}`
      return { 
        success: false, 
        error: errorMessage 
      }
    }
  }

  const handleTestConnection = async (connection: ExtendedSystemConnection) => {
    if (!fpCode) return

    // Mark connection as being tested
    setTestingConnections(prev => ({ ...prev, [connection.id]: true }))
    
    try {
      debugLog(`🔍 Testing ${connection.system_name} connection for ${fpCode}`)
      
      // Call the systems API to test the connection
      const response = await apiClient.post('/systems/connections/test', null, {
        params: {
          fp_code: fpCode,
          systems: [connection.system_name]
        }
      })
      
      const testResult = response.data
      
      if (testResult.success && testResult.data && testResult.data.connections) {
        const systemResult = testResult.data.connections[connection.system_name]
        
        if (systemResult) {
          if (systemResult.status === 'connected') {
            // Test successful
            setTestResults(prev => ({
              ...prev,
              [connection.id]: {
                success: true,
                message: `✅ ${connection.system_name.charAt(0).toUpperCase() + connection.system_name.slice(1)} connection is working properly${systemResult.organization_name ? ` (${systemResult.organization_name})` : ''}`
              }
            }))
            
            // Refresh data to update last_tested_at
            refetch()
          } else {
            // Test failed
            setTestResults(prev => ({
              ...prev,
              [connection.id]: {
                success: false,
                message: `❌ ${connection.system_name.charAt(0).toUpperCase() + connection.system_name.slice(1)} connection test failed: ${systemResult.error_message || 'Unknown error'}`
              }
            }))
          }
        } else {
          throw new Error(`No test result found for ${connection.system_name}`)
        }
      } else {
        throw new Error(testResult.message || 'Test failed with no results')
      }
      
    } catch (error: any) {
      console.error(`❌ Connection test failed:`, error)
      
      setTestResults(prev => ({
        ...prev,
        [connection.id]: {
          success: false,
          message: `❌ Connection test failed: ${error.response?.data?.detail || error.message || 'Unknown error'}`
        }
      }))
    } finally {
      // Remove testing state
      setTestingConnections(prev => {
        const updated = { ...prev }
        delete updated[connection.id]
        return updated
      })
      
      // Clear test results after 5 seconds
      setTimeout(() => {
        setTestResults(prev => {
          const updated = { ...prev }
          delete updated[connection.id]
          return updated
        })
      }, 5000)
    }
  }

  const handleReauthorizeClick = (connection: ExtendedSystemConnection) => {
    setReauthorizeConnection(connection)
    setShowReauthorizeDialog(true)
  }

  const handleReauthorizeConfirm = async () => {
    if (!reauthorizeConnection || !fpCode) return

    setReauthorizeLoading(true)
    try {
      debugLog(`Starting re-authorization for ${fpCode}`)
      
      // Call the new reauthorize endpoint
      const response = await apiClient.post(`/connections/xero/reauthorize/${fpCode}`)
      debugLog('✅ Reauthorize initiated')
      
      if (response.data?.success && response.data?.data?.authorization_url) {
        // Redirect to the new OAuth URL
        window.location.href = response.data.data.authorization_url
      } else {
        throw new Error('Invalid response from reauthorize endpoint')
      }
    } catch (err: any) {
      console.error('Error during re-authorization:', err)
      setConnectionAlert({
        type: 'error',
        message: `Re-authorization failed: ${err.response?.data?.detail || err.message}`
      })
      setReauthorizeLoading(false)
      setShowReauthorizeDialog(false)
    }
  }

  const handleReauthorizeCancel = () => {
    if (!reauthorizeLoading) {
      setShowReauthorizeDialog(false)
      setReauthorizeConnection(null)
    }
  }

  const handleDisconnectClick = (connection: ExtendedSystemConnection) => {
    setDisconnectConnection(connection)
    setShowDisconnectDialog(true)
  }

  const handleDisconnectConfirm = async () => {
    if (!disconnectConnection || !fpCode) return

    // Mark connection as being disconnected
    setDisconnectingConnections(prev => ({ ...prev, [disconnectConnection.id]: true }))

    try {
      debugLog(`🗑️ Disconnecting ${disconnectConnection.system_name} for ${fpCode}`)
      
      // Call the disconnect endpoint
      const response = await apiClient.delete(`/connections/${disconnectConnection.system_name}/disconnect/${fpCode}`)
      
      if (response.data?.success) {
        setConnectionAlert({
          type: 'success',
          message: `✅ ${getSystemDisplayName(disconnectConnection.system_name)} has been disconnected successfully`
        })
        
        // Refresh data to remove the disconnected one
        refetch()
        
        // Clear alert after 5 seconds
        setTimeout(() => setConnectionAlert(null), 5000)
      } else {
        throw new Error(response.data?.message || 'Disconnect failed')
      }
      
    } catch (error: any) {
      console.error(`❌ Disconnect failed:`, error)
      
      setConnectionAlert({
        type: 'error',
        message: `❌ Failed to disconnect ${getSystemDisplayName(disconnectConnection.system_name)}: ${error.response?.data?.detail || error.message || 'Unknown error'}`
      })
    } finally {
      // Remove disconnecting state
      setDisconnectingConnections(prev => {
        const updated = { ...prev }
        delete updated[disconnectConnection.id]
        return updated
      })
      
      // Close dialog
      setShowDisconnectDialog(false)
      setDisconnectConnection(null)
    }
  }

  const handleDisconnectCancel = () => {
    if (!disconnectingConnections[disconnectConnection?.id || 0]) {
      setShowDisconnectDialog(false)
      setDisconnectConnection(null)
    }
  }

  const maskSecretValue = (value: string) => {
    if (!value || value.length < 8) return '••••••••'
    return value.substring(0, 4) + '••••' + value.substring(value.length - 4)
  }

  const getSystemIcon = (systemName: string) =>
    getSharedSystemIcon(systemName, { size: 32 }) ?? (
      <SettingsIcon sx={{ width: 32, height: 32, color: poolwerxColors.grey[500] }} />
    )

  const getSystemDisplayName = (systemName: string) => {
    switch (systemName.toLowerCase()) {
      case 'lightspeed':
        return 'Lightspeed'
      case 'xero':
        return 'Xero'
      case 'skimmer':
        return 'Skimmer'
      default:
        return systemName.charAt(0).toUpperCase() + systemName.slice(1)
    }
  }

  const formatDate = (dateString: string) => {
    return formatInUserTZ(dateString, {
      format: 'dd MMM yyyy, hh:mm a',
      includeTimezone: true,
      fallback: 'Invalid date'
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !franchisePartner) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">
          {error || 'Franchise partner not found'}
        </Typography>
        <Button
          component={RouterLink}
          to={returnUrl}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Franchise Partners
        </Button>
      </Box>
    )
  }

  return (
    <>
      <AdminPageHeader
        pageKey="manageConnections"
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Franchise Partners', to: '/admin/franchise-partners' },
          { label: franchisePartner.franchise_name, to: generateAdminFpUrl(franchisePartner.id, 'edit') },
          { label: 'Connections' },
        ]}
        entityChip={franchisePartner.franchise_name}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
            sx={{
              backgroundColor: poolwerxColors.teal,
              '&:hover': { backgroundColor: poolwerxColors.teal, opacity: 0.9 }
            }}
          >
            Connect a system
          </Button>
        }
      />


      {/* Connection Test Alert */}
      {connectionAlert && (
        <Alert 
          severity={connectionAlert.type} 
          sx={{ mb: 3 }}
          onClose={() => setConnectionAlert(null)}
        >
          {connectionAlert.message}
        </Alert>
      )}

      {/* Connections Grid */}
      <Grid container spacing={3}>
        {connections.map((connection) => (
          <Grid item xs={12} md={6} lg={4} key={connection.id}>
            <Card>
              <CardContent>
                {/* System Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  {getSystemIcon(connection.system_name)}
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">
                        {getSystemDisplayName(connection.system_name)}
                      </Typography>
                      {/* System Integrations button for Xero */}
                      {connection.system_name === 'xero' && connection.metadata.short_code && (
                        <Tooltip title="System Integrations">
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.preventDefault()
                              const xeroUrl = generateXeroAppUrl(connection.metadata.short_code!)
                              openXeroLink(xeroUrl, 'Xero Organization')
                            }}
                            sx={{
                              color: poolwerxColors.teal,
                              padding: '6px',
                              '&:hover': {
                                color: poolwerxColors.teal,
                                opacity: 0.8
                              }
                            }}
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* System Integrations button for Lightspeed */}
                      {connection.system_name === 'lightspeed' && (connection.metadata?.store_code || connection.connection_id) && (
                        <Tooltip title="System Integrations">
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.preventDefault()
                              // Use store_code if available, otherwise use connection_id as the subdomain
                              const subdomain = connection.metadata?.store_code || connection.connection_id
                              const lightspeedUrl = generateLightspeedTokensUrl(subdomain)
                              openLightspeedLink(lightspeedUrl, 'Personal Tokens')
                            }}
                            sx={{
                              color: poolwerxColors.teal,
                              padding: '6px',
                              '&:hover': {
                                color: poolwerxColors.teal,
                                opacity: 0.8
                              }
                            }}
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Top right - Connection status and last tested */}
                  <Box sx={{ textAlign: 'right', minWidth: '140px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', mt: 0, mb: '-3px' }}>
                      {(connection.connection_status === 'active' || connection.status === 'connected') ? (
                        <ConnectedIcon sx={{ color: 'success.main', fontSize: '1rem' }} />
                      ) : connection.connection_status === 'reauthorization_required' ? (
                        <IncompleteIcon sx={{ color: 'warning.main', fontSize: '1rem' }} />
                      ) : (connection.connection_status === 'error' || connection.status === 'incomplete') ? (
                        <DisconnectedIcon sx={{ color: 'error.main', fontSize: '1rem' }} />
                      ) : (
                        <IncompleteIcon sx={{ color: 'warning.main', fontSize: '1rem' }} />
                      )}
                      <Tooltip title={connection.secret_name} placement="top">
                        <Typography variant="body2" color="text.secondary" sx={{ cursor: 'help', fontWeight: 500, fontSize: '0.9em' }}>
                          {(connection.connection_status === 'active' || connection.status === 'connected') ? 'Connected' :
                           connection.connection_status === 'reauthorization_required' ? 'Re-authorization Required' :
                           (connection.connection_status === 'error' || connection.status === 'incomplete') ? 'Connection Error' :
                           'Incomplete Setup'}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {connection.last_tested_at ? formatDate(connection.last_tested_at) : 'Never tested'}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Connection Metadata */}
                <Stack spacing={1.5}>
                  {/* Error Message - moved to top */}
                  {connection.error_message && (
                    <Box>
                      <Alert 
                        severity={connection.connection_status === 'reauthorization_required' ? 'warning' : 'error'}
                        sx={{ fontSize: '0.8rem', py: 0.5 }}
                      >
                        {connection.error_message}
                      </Alert>
                    </Box>
                  )}

                  {/* Connection ID */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Connection ID
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      value={connection.connection_id}
                      disabled
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                  </Box>

                  {/* Organization Name (Xero only) */}
                  {connection.system_name === 'xero' && connection.metadata.tenant_name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Organization Name
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={connection.metadata.tenant_name || ''}
                        disabled
                      />
                    </Box>
                  )}


                  {/* Test Results */}
                  {testResults[connection.id] && (
                    <Box>
                      <Alert 
                        severity={testResults[connection.id].success ? 'success' : 'error'}
                        sx={{ fontSize: '0.8rem', py: 0.5 }}
                      >
                        {testResults[connection.id].message}
                      </Alert>
                    </Box>
                  )}

                  {/* Connection Actions */}
                  <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Stack spacing={1}>
                      {/* Special action buttons based on connection status */}
                      {connection.connection_status === 'reauthorization_required' && (
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          onClick={() => handleReauthorizeClick(connection)}
                          disabled={reauthorizeLoading}
                          sx={{
                            backgroundColor: poolwerxColors.warning,
                            color: 'white',
                            '&:hover': {
                              backgroundColor: poolwerxColors.warning,
                              opacity: 0.9
                            }
                          }}
                        >
                          Re-authorize Connection
                        </Button>
                      )}
                      
                      {connection.system_name !== 'skimmer' &&
                        (connection.status === 'incomplete' || connection.connection_status === 'error') && (
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          onClick={() => {
                            // Start OAuth flow for incomplete connections
                            if (connection.system_name === 'xero') {
                              window.location.href = `/api/connections/xero/connect/${fpCode}`
                            }
                          }}
                          sx={{
                            backgroundColor: poolwerxColors.teal,
                            '&:hover': {
                              backgroundColor: poolwerxColors.teal,
                              opacity: 0.9
                            }
                          }}
                        >
                          {connection.connection_status === 'error' ? 'Fix Connection' : 'Complete Setup'}
                        </Button>
                      )}
                      
                      {/* Edit button for Lightspeed and Skimmer (API key based systems) */}
                      {(connection.system_name === 'lightspeed' || connection.system_name === 'skimmer') && (
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          onClick={() => {
                            setEditingConnection(connection)
                            setShowAddModal(true)
                          }}
                          sx={{
                            backgroundColor: poolwerxColors.teal,
                            '&:hover': {
                              backgroundColor: poolwerxColors.teal,
                              opacity: 0.9
                            }
                          }}
                        >
                          Edit Connection
                        </Button>
                      )}
                      
                      {/* Test (non-Skimmer only - Skimmer worker is dormant) and Disconnect */}
                      <Stack direction="row" spacing={1}>
                        {connection.system_name !== 'skimmer' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleTestConnection(connection)}
                            disabled={testingConnections[connection.id]}
                            sx={{ flex: 1 }}
                            startIcon={testingConnections[connection.id] ? <CircularProgress size={16} /> : undefined}
                          >
                            {testingConnections[connection.id] ? 'Testing...' : 'Test connection'}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          sx={{ flex: 1 }}
                          onClick={() => handleDisconnectClick(connection)}
                          disabled={disconnectingConnections[connection.id]}
                          startIcon={disconnectingConnections[connection.id] ? <CircularProgress size={16} /> : undefined}
                        >
                          {disconnectingConnections[connection.id] ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>


                  {/* Scopes (for OAuth connections) */}
                  {connection.metadata.scopes && connection.metadata.scopes.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Permissions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {connection.metadata.scopes.map((scope, index) => (
                          <Chip
                            key={index}
                            label={scope}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}


                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* No Connections State */}
        {connections.length === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SettingsIcon sx={{ fontSize: '4rem', color: poolwerxColors.grey[400], mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No system connections configured
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Connect external systems like Lightspeed, Xero, or Skimmer to enable data synchronization.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddModal(true)}
                  sx={{
                    backgroundColor: poolwerxColors.teal,
                    '&:hover': {
                      backgroundColor: poolwerxColors.teal,
                      opacity: 0.9
                    }
                  }}
                >
                  Connect your first system
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Connect System Modal */}
      <ConnectSystemModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false)
          setEditingConnection(null)
        }}
        fpCode={fpCode || ''}
        franchiseName={franchisePartner?.franchise_name || ''}
        existingConnections={editingConnection ? connectedSystems.filter(s => s !== editingConnection.system_name) : connectedSystems}
        onConnect={handleConnect}
        editingConnection={editingConnection}
      />

      {/* Xero Re-authorization Dialog */}
      <XeroReauthorizeDialog
        open={showReauthorizeDialog}
        onClose={handleReauthorizeCancel}
        onConfirm={handleReauthorizeConfirm}
        fpCode={fpCode || ''}
        franchiseName={franchisePartner?.franchise_name || ''}
        tenantName={reauthorizeConnection?.metadata?.tenant_name}
        loading={reauthorizeLoading}
      />

      {/* Disconnect Dialog */}
      <DisconnectDialog
        open={showDisconnectDialog}
        onClose={handleDisconnectCancel}
        onConfirm={handleDisconnectConfirm}
        fpCode={fpCode || ''}
        franchiseName={franchisePartner?.franchise_name || ''}
        systemDisplayName={getSystemDisplayName(disconnectConnection?.system_name || '')}
        tenantName={disconnectConnection?.metadata?.tenant_name || disconnectConnection?.metadata?.store_code}
        loading={disconnectingConnections[disconnectConnection?.id || 0] || false}
      />
    </>
  )
}

export default ManageConnections